import { combine, createEffect, createEvent, createStore, sample } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { appMounted } from "@features/app/model";
import { soundEffectsToggled } from "@features/shared/events";
import { soundPlayer } from "@utils/sound/player";
import type { SoundName } from "@utils/sound/sounds";
import type { ITaskLayoutUIContract } from "@features/tasks/ui/task-layout";
import type { ITasksContract } from "@features/tasks/model";
import type { ILessonCompletionContract } from "@features/lesson-completion/model";
import type { ILessonCompletionFlowContract } from "@features/lesson-completion/ui";
import type { ICurrentLessonContract } from "@features/lessons/ui/current";
import type { IAmplitudeFlagsContract } from "@features/amplitude-flags/model";
import { canPlay, readSoundPref, soundForAnswerCheck, soundForCompletionScreen, writeSoundPref } from "./rules";

const soundToggled = createEvent<boolean>();
const soundRequested = createEvent<SoundName>();

const $soundPref = createStore<boolean>(readSoundPref()).on(soundToggled, (_, enabled) => enabled);
const $gamificationEnabled = createStore<boolean | null>(null);
const $soundEnabled = combine($soundPref, $gamificationEnabled, (pref, flag) => pref && flag === true);

const playSoundFx = createEffect((name: SoundName) => {
    soundPlayer.play(name);
});
const armSoundUnlockFx = createEffect(() => {
    soundPlayer.armUnlock();
});
const persistSoundPrefFx = createEffect((enabled: boolean) => {
    writeSoundPref(enabled);
});
const trackSoundToggledFx = createEffect((enabled: boolean) => {
    soundEffectsToggled(enabled);
});

// Single choke point: every trigger emits soundRequested, only this sample reaches the player.
sample({
    clock: soundRequested,
    source: $soundEnabled,
    filter: (enabled) => enabled && canPlay(),
    fn: (_, name) => name,
    target: playSoundFx
});

sample({ clock: soundToggled, target: [persistSoundPrefFx, trackSoundToggledFx] });
// Turning sound on is itself a gesture: unlock the context and let the student hear the result.
sample({ clock: soundToggled, filter: (enabled) => enabled, target: armSoundUnlockFx });
sample({ clock: soundToggled, filter: (enabled) => enabled, fn: (): SoundName => "correct", target: soundRequested });

const contract = new ModelContract(
    { $soundPref, $soundEnabled },
    { soundToggled, soundRequested },
    { playSoundFx, armSoundUnlockFx, persistSoundPrefFx, trackSoundToggledFx },
    {}
);

export type ISoundContract = typeof contract;

export class SoundModule extends ModuleDescription<ISoundContract> {
    constructor(
        public taskLayoutUIModule: ITaskLayoutUIContract,
        public tasksModule: ITasksContract,
        public lessonCompletionModule: ILessonCompletionContract,
        public lessonCompletionFlowModule: ILessonCompletionFlowContract,
        public currentLessonModule: ICurrentLessonContract,
        public amplitudeFlagsModule: IAmplitudeFlagsContract
    ) {
        super();
    }

    getDefaultContract() {
        return contract;
    }

    run() {
        sample({
            clock: [this.amplitudeFlagsModule.stores.$gamificationEnabled, appMounted],
            source: this.amplitudeFlagsModule.stores.$gamificationEnabled,
            target: $gamificationEnabled
        });

        // Arm the mobile unlock as soon as a lesson is open: whatever the student taps first
        // resumes the context and preloads the files, long before the first "Check answer".
        sample({
            clock: [this.currentLessonModule.stores.$currentLessonId.updates, $soundEnabled.updates],
            source: { lessonId: this.currentLessonModule.stores.$currentLessonId, enabled: $soundEnabled },
            filter: ({ lessonId, enabled }) => lessonId !== null && enabled,
            fn: () => undefined,
            target: armSoundUnlockFx
        });

        // Tasks checked on the client (all 8 TaskLayout types). Restored answers never pass through here.
        sample({
            clock: this.taskLayoutUIModule.events.taskAnswerChecked,
            filter: (check) => !check.usesSavedAnswer,
            fn: soundForAnswerCheck,
            target: soundRequested
        });

        // Legacy server-checked quiz blocks.
        sample({
            clock: this.tasksModule.effects.taskCheckedFx.done,
            fn: ({ result }) => soundForAnswerCheck({ isCorrect: result.status === "completed" }),
            target: soundRequested
        });

        // Plays as the "lesson done" (or daily goal) screen becomes ready, for first and repeat completions alike.
        sample({
            clock: this.lessonCompletionModule.events.lessonCompletionSettled,
            fn: (): SoundName => "lessonComplete",
            target: soundRequested
        });

        sample({
            clock: this.lessonCompletionFlowModule.stores.$currentScreen.updates,
            filter: (screen) => soundForCompletionScreen(screen) !== null,
            fn: (screen) => soundForCompletionScreen(screen) as SoundName,
            target: soundRequested
        });
    }
}
